import {
  Activity,
  Bot,
  CheckCircle,
  DollarSign,
  GitBranch,
  TrendingUp,
  Zap,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

interface KPIMetricsSummary {
  total_agents: number
  total_task_runs: number
  total_workflow_runs: number
  total_outcome_runs: number
  total_revenue_usd: number
  total_cost_usd: number
  total_margin_usd: number
}

interface KPIMetricsHeaderProps {
  summary: KPIMetricsSummary
  loading?: boolean
}

export function KPIMetricsHeader({
  summary,
  loading = false,
}: KPIMetricsHeaderProps) {
  const formatNumber = (num: number) => {
    if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1)}M`
    }
    if (num >= 1_000) {
      return `${(num / 1_000).toFixed(0)}k`
    }
    return num.toString()
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(2)}M`
    }
    if (amount >= 1_000) {
      return `$${(amount / 1_000).toFixed(0)}k`
    }
    return `$${amount.toLocaleString()}`
  }

  const cards = [
    {
      title: 'Total Agents',
      value: `${summary.total_agents}`,
      sub: 'configured agents',
      icon: Bot,
      accent: {
        badgeBg: 'bg-blue-50 dark:bg-blue-950/40',
        badgeRing: 'ring-1 ring-blue-200/70 dark:ring-blue-900/40',
        icon: 'text-blue-600 dark:text-blue-400',
      },
    },
    {
      title: 'Task Runs',
      value: formatNumber(summary.total_task_runs),
      sub: 'individual tasks',
      icon: Activity,
      accent: {
        badgeBg: 'bg-emerald-50 dark:bg-emerald-950/40',
        badgeRing: 'ring-1 ring-emerald-200/70 dark:ring-emerald-900/40',
        icon: 'text-emerald-600 dark:text-emerald-400',
      },
    },
    {
      title: 'Workflow Runs',
      value: formatNumber(summary.total_workflow_runs),
      sub: 'workflow processes',
      icon: GitBranch,
      accent: {
        badgeBg: 'bg-purple-50 dark:bg-purple-950/40',
        badgeRing: 'ring-1 ring-purple-200/70 dark:ring-purple-900/40',
        icon: 'text-purple-600 dark:text-purple-400',
      },
    },
    {
      title: 'Outcome Runs',
      value: formatNumber(summary.total_outcome_runs),
      sub: 'successful outcomes',
      icon: CheckCircle,
      accent: {
        badgeBg: 'bg-emerald-50 dark:bg-emerald-950/40',
        badgeRing: 'ring-1 ring-emerald-200/70 dark:ring-emerald-900/40',
        icon: 'text-emerald-600 dark:text-emerald-400',
      },
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(summary.total_revenue_usd),
      sub: 'generated revenue',
      icon: DollarSign,
      accent: {
        badgeBg: 'bg-sky-50 dark:bg-sky-950/40',
        badgeRing: 'ring-1 ring-sky-200/70 dark:ring-sky-900/40',
        icon: 'text-sky-600 dark:text-sky-400',
      },
    },
    {
      title: 'Cost',
      value: formatCurrency(summary.total_cost_usd),
      sub: 'total costs',
      icon: Zap,
      accent: {
        badgeBg: 'bg-rose-50 dark:bg-rose-950/40',
        badgeRing: 'ring-1 ring-rose-200/70 dark:ring-rose-900/40',
        icon: 'text-rose-600 dark:text-rose-400',
      },
    },
    {
      title: 'Margin',
      value: formatCurrency(summary.total_margin_usd),
      sub: 'profit margin',
      icon: TrendingUp,
      accent: {
        badgeBg: 'bg-indigo-50 dark:bg-indigo-950/40',
        badgeRing: 'ring-1 ring-indigo-200/70 dark:ring-indigo-900/40',
        icon: 'text-indigo-600 dark:text-indigo-400',
      },
    },
  ] as const

  if (loading) {
    return (
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2'>
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            key={i}
            className='relative isolate rounded-xl border bg-card p-6 overflow-hidden shadow-sm'
          >
            <div className='animate-pulse space-y-2'>
              <div className='h-3 w-24 rounded bg-muted' />
              <div className='h-6 w-20 rounded bg-muted' />
              <div className='h-3 w-28 rounded bg-muted/70' />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2'>
      {cards.map((c, i) => {
        const Icon = c.icon
        return (
          <article
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            key={i}
            className={cn(
              'group relative isolate rounded-xl border bg-card',
              'p-6 transition-all focus-within:ring-2 focus-within:ring-ring hover:shadow-md hover:border-border/80',
              'shadow-sm'
            )}
            aria-label={`${c.title} ${c.value}`}
          >
            <div className='flex items-center justify-between'>
              <h3 className='text-sm font-medium text-muted-foreground tracking-tight'>
                {c.title}
              </h3>
              <span
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-md backdrop-blur-sm',
                  c.accent.badgeBg,
                  c.accent.badgeRing,
                  'shadow-sm'
                )}
                aria-hidden
              >
                <Icon className={cn('h-3.5 w-3.5', c.accent.icon)} />
              </span>
            </div>

            <div className='mt-2 flex items-baseline gap-2'>
              <div className='text-xl md:text-2xl font-semibold leading-none tracking-tight'>
                {c.value}
              </div>
              <span className='text-sm text-muted-foreground leading-none'>
                {c.sub}
              </span>
            </div>
          </article>
        )
      })}
    </div>
  )
}
